import React, { createContext, useContext, useState, ReactNode, useCallback, useRef } from 'react';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { api } from '../services/api';

interface User {
  id: string;
  role: 'admin' | 'staff';
  name: string;
  mobileNumber: string;
  date_of_birth?: string | Date;
}

interface LoginResponse {
  token: string;
  require2FA?: boolean;
  date_of_birth?: string;
  requireDOB?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (mobileNumber: string, password_or_otp: string, totp_code?: string) => Promise<LoginResponse>;
  logout: () => void;
  requestOtp: (mobileNumber: string) => Promise<void>;
  verifyOtp: (mobileNumber: string, otp: string) => Promise<void>;
  setAuthToken: (token: string | null) => void;
  isAuthenticated: boolean;
  loading: boolean;
  updateDOB: (date_of_birth: string) => Promise<any>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    if (user) {
      inactivityTimerRef.current = setTimeout(() => {
        logout();
        alert('Session expired due to inactivity. Please login again.');
      }, INACTIVITY_TIMEOUT);
    }
  }, [user]);

  // Setup activity listeners
  React.useEffect(() => {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const resetTimer = () => {
      if (user) {
        resetInactivityTimer();
      }
    };

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    // Start timer when user is logged in
    if (user) {
      resetInactivityTimer();
    }

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [user, resetInactivityTimer]);

  // Persist user session on mount - but require fresh login every time
  React.useEffect(() => {
    // For better security, we'll not auto-restore session
    // User must login every time they open the application
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode<User & JwtPayload>(token);
        // Check for token expiry
        if (decoded.exp && Date.now() >= decoded.exp * 1000) {
          sessionStorage.removeItem('token');
          setUser(null);
        } else {
          setUser(decoded);
          setIsAuthenticated(true);
        }
      } catch {
        sessionStorage.removeItem('token');
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  const login = async (mobileNumber: string, password_or_otp: string, totp_code?: string): Promise<LoginResponse> => {
    try {
      const payload: any = { mobileNumber };
      if (totp_code) {
        payload.totp_code = totp_code;
        payload.password_or_otp = password_or_otp; // Include password with 2FA code
      } else {
        payload.password_or_otp = password_or_otp;
      }

      const response = await api.post<LoginResponse>('/auth/login', payload);
      
      const { token, require2FA, date_of_birth } = response.data;

      // If login is successful and doesn't require 2FA, set the user
      if (token && !require2FA) {
        sessionStorage.setItem('token', token);
        const decodedUser: User = jwtDecode(token);
        // Patch in date_of_birth if present in response (for immediate UI update)
        if (date_of_birth) decodedUser.date_of_birth = date_of_birth;
        setUser(decodedUser);
        setIsAuthenticated(true);
        resetInactivityTimer();
      }

      return response.data;
    } catch (err: any) {
      throw err;
    }
  };

  const requestOtp = async (mobileNumber: string) => {
    try {
      await api.post('/staff/request-otp', { mobileNumber });
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Failed to request OTP');
    }
  };

  const verifyOtp = async (mobileNumber: string, otp: string) => {
    try {
      await api.post('/staff/verify-otp', { mobileNumber, otp });
      // On successful verification, you might want to auto-login or prompt for password
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'OTP verification failed');
    }
  };

  const logout = useCallback(() => {
    sessionStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const setAuthToken = useCallback((token: string | null) => {
    console.log('setAuthToken called with token:', token ? 'token exists' : 'null');
    if (token) {
      sessionStorage.setItem('token', token);
      try {
        const decoded = jwtDecode<User>(token);
        console.log('Token decoded successfully, user:', decoded);
        setUser(decoded);
        setIsAuthenticated(true);
        resetInactivityTimer();
      } catch (error) {
        console.error('Failed to decode token:', error);
        sessionStorage.removeItem('token');
        setUser(null);
        setIsAuthenticated(false);
      }
    } else {
      sessionStorage.removeItem('token');
      setUser(null);
      setIsAuthenticated(false);
    }
  }, [resetInactivityTimer]);

  // Add a function to update DOB and user context
  const updateDOB = useCallback(async (date_of_birth: string) => {
    try {
      const response = await api.post<{ date_of_birth: string }>('/user/dob', { date_of_birth });
      if (user) {
        setUser({ ...user, date_of_birth: response.data.date_of_birth });
      }
      return response.data;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred while updating date of birth');
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, logout, requestOtp, verifyOtp, setAuthToken, isAuthenticated, loading, updateDOB }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 